import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconFileModalCourses,
  iconQuizErrorStudent,
  iconQuizSmallModalCourses,
  iconQuizSuccessStudent,
} from '../../assets';
import { useTheme } from '../../theme';
import type { VimeoPlayerProgress } from '../VimeoPlayer';
import VimeoPlayer from '../VimeoPlayer';
import type { StudentCoursePlayerActivityType } from './StudentCoursePlayerActivitiesSidebar';

export type DownloadableResourceFileKind = 'pdf' | 'xlsx' | 'docx';

export interface DownloadableResourceItem {
  fileName: string;
  sizeLabel: string;
  kind: DownloadableResourceFileKind;
  fileUrl?: string;
}

export interface QuizQuestionItem {
  prompt: string;
  options: string[];
  questionId?: string;
  optionIds?: string[];
}

/** Uma resposta para POST submit-quiz (`answers` no body). */
export interface QuizSubmitAnswerPayload {
  questionId: string;
  selectedOptionId: string;
}

/** Resposta de POST submit-quiz (mapear da API). */
export interface QuizSubmitResultPayload {
  approved: boolean;
  yourScore: number;
  minimumRequiredScore: number;
  xpEarned: number;
  remainingAttempts: number;
  totalAttempts: number;
}

/** Resposta de GET quiz-pre-attempt (card antes de iniciar). */
export interface QuizPreAttemptPayload {
  totalQuestions: number;
  minScore: number;
  maxAttempts: number;
  remainingAttempts: number;
}

export interface StudentCoursePlayerContentProps {
  /** Tipo da atividade selecionada: vídeo mantém o player mock; `resource` exibe lista de downloads. */
  activityType: StudentCoursePlayerActivityType;
  lessonTitle: string;
  durationLabel: string;
  xpLabel: string;
  /** Texto do botão principal (concluir / continuar). */
  actionLabel: string;
  /** Parágrafo abaixo do título (material de apoio). */
  resourceDescription?: string;
  /** Lista de arquivos quando `activityType === 'resource'`. */
  downloadableResources?: DownloadableResourceItem[];
  /** Quiz: nota mínima para aprovação (%). */
  quizPassingScorePercent?: number;
  /** Quiz: tentativas máximas. */
  quizMaxAttempts?: number;
  /** Quiz: título dentro do card inicial. */
  quizIntroHeadline?: string;
  /** Quiz: descrição abaixo do título do card inicial. */
  quizIntroDescription?: string;
  /** Lista de questões (múltipla escolha) quando nao ha `quizFetchQuestions`. */
  quizQuestions?: QuizQuestionItem[];
  /**
   * Se definido, as questoes sao carregadas ao clicar em Start Assessment (ex.: POST quiz-attempts).
   * Enquanto isso, a intro nao exibe a contagem de questoes ate a resposta chegar.
   */
  quizFetchQuestions?: () => Promise<QuizQuestionItem[]>;
  /**
   * GET quiz-pre-attempt: preenche o card intro (questoes, nota minima, tentativas).
   * Sem tentativas restantes, o botao Start some e um aviso em vermelho e exibido.
   */
  quizFetchPreAttempt?: () => Promise<QuizPreAttemptPayload>;
  startAssessmentLabel?: string;
  nextQuestionLabel?: string;
  submitQuizLabel?: string;
  previousLessonLabel?: string;
  /** URL do Vimeo para aulas de video. */
  vimeoUrl?: string;
  /** Ponto inicial em segundos para retomar a aula de video. */
  initialVideoTimeSeconds?: number;
  /** Percentual inicial para retomada quando nao houver tempo salvo. */
  initialVideoProgressPercentage?: number;
  onVideoProgress?: (progress: VimeoPlayerProgress) => void;
  onPreviousLesson?: () => void;
  onMarkComplete?: () => void;
  /**
   * Chamado ao clicar em Submit na última questão (ex.: POST submit-quiz com o attemptId da API).
   * Recebe `answers` no formato da API. Se rejeitar, o quiz permanece na última questão.
   */
  quizOnSubmit?: (
    answers: QuizSubmitAnswerPayload[]
  ) => Promise<QuizSubmitResultPayload>;
  /** Última lição do curso (define texto "Finish Course" no resultado aprovado). */
  isLastLessonInCourse?: boolean;
  /** Após aprovação: avançar para a próxima lição (se não for a última). */
  quizOnNextLesson?: () => void;
  /** Após aprovação na última lição (ou fluxo equivalente). */
  quizOnFinishCourse?: () => void;
}

const DEFAULT_RESOURCE_DESCRIPTION =
  'Access essential technical documentation, product manuals, and reference materials for FerAppease and BRD management protocols.';

const DEFAULT_QUIZ_QUESTIONS: QuizQuestionItem[] = [
  {
    prompt: 'What is the main benefit of FerAppease?',
    options: [
      'Increases weight gain',
      'Reduces stress and anxiety during procedures',
      'Treats respiratory infections',
      'Improves milk production',
    ],
  },
  {
    prompt: 'Which pathogen is most commonly associated with BRD?',
    options: [
      'Mannheimia haemolytica',
      'E. coli',
      'Salmonella',
      'Clostridium perfringens',
    ],
  },
  {
    prompt: 'What is a primary sign of respiratory distress in cattle?',
    options: [
      'Increased respiratory rate',
      'Excessive salivation only',
      'Blindness',
      'Lameness',
    ],
  },
  {
    prompt:
      'When is vaccination typically most effective in a BRD prevention program?',
    options: [
      'Before high-risk periods (e.g., weaning, transport)',
      'Only after clinical signs appear',
      'Never in calves',
      'Only in adult bulls',
    ],
  },
  {
    prompt: 'What role does stress play in BRD development?',
    options: [
      'It can suppress immunity and increase susceptibility',
      'It has no effect',
      'It only affects dairy breeds',
      'It eliminates the need for treatment',
    ],
  },
];

function FileTypeBadge({ kind }: { kind: DownloadableResourceFileKind }) {
  const theme = useTheme();
  const fileBadge = theme.palette.studentCoursePlayer.fileBadge;
  const spec: Record<
    DownloadableResourceFileKind,
    { bg: string; label: string; color: string }
  > = {
    pdf: { bg: fileBadge.pdfBackground, label: 'PDF', color: fileBadge.text },
    xlsx: { bg: fileBadge.xlsxBackground, label: 'X', color: fileBadge.text },
    docx: { bg: fileBadge.docxBackground, label: 'W', color: fileBadge.text },
  };
  const s = spec[kind];
  return (
    <div
      aria-hidden
      style={{
        width: 40,
        height: 48,
        borderRadius: theme.shape.borderRadius.sm,
        backgroundColor: s.bg,
        color: s.color,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: theme.typography.fontWeight.bold,
        flexShrink: 0,
      }}
    >
      <span style={{ fontSize: 11, lineHeight: 1 }}>{s.label}</span>
    </div>
  );
}

type QuizPhase = 'intro' | 'active' | 'done' | 'result';

function formatScorePercent(value: number): string {
  return `${Math.round(value)}%`;
}

function QuizResultSuccessView({
  result,
  isLastLessonInCourse,
  primary,
  contrast,
  theme,
  onPrimary,
}: {
  result: QuizSubmitResultPayload;
  isLastLessonInCourse: boolean;
  primary: string;
  contrast: string;
  theme: ReturnType<typeof useTheme>;
  onPrimary: () => void;
}) {
  const { t } = useTranslation();
  const quizTokens = theme.palette.studentCoursePlayer.quiz;
  const primaryLabel = isLastLessonInCourse
    ? t('student_course_player.quiz.finish_course')
    : t('student_course_player.quiz.next_lesson');
  return (
    <div
      style={{
        maxWidth: 520,
        margin: '0 auto',
        padding: theme.spacing.lg,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 96,
          height: 96,
          margin: `0 auto ${theme.spacing.lg}`,
          borderRadius: '50%',
          backgroundColor: quizTokens.successBackground,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={iconQuizSuccessStudent}
          alt=""
          width={56}
          height={56}
          style={{ display: 'block', objectFit: 'contain' }}
        />
      </div>
      <h2
        style={{
          margin: 0,
          fontSize: theme.typography.fontSize['3xl'],
          fontWeight: theme.typography.fontWeight.bold,
          color: quizTokens.successText,
        }}
      >
        {t('student_course_player.quiz.result_success_title')}
      </h2>
      <p
        style={{
          marginTop: theme.spacing.sm,
          marginBottom: theme.spacing.lg,
          color: quizTokens.mutedText,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {t('student_course_player.quiz.result_success_description')}
      </p>
      <div
        style={{
          borderRadius: theme.shape.borderRadius.md,
          border: `1px solid ${quizTokens.successText}`,
          backgroundColor: quizTokens.successBackground,
          padding: theme.spacing.lg,
          marginBottom: theme.spacing.lg,
          textAlign: 'left',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: theme.spacing.lg,
            marginBottom: theme.spacing.md,
          }}
        >
          <div>
            <div
              style={{
                fontSize: theme.typography.fontSize.sm,
                color: quizTokens.mutedText,
                marginBottom: theme.spacing.xs,
              }}
            >
              {t('student_course_player.quiz.your_score')}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: theme.typography.fontWeight.bold,
                color: quizTokens.successText,
              }}
            >
              {formatScorePercent(result.yourScore)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div
              style={{
                fontSize: theme.typography.fontSize.sm,
                color: quizTokens.mutedText,
                marginBottom: theme.spacing.xs,
              }}
            >
              {t('student_course_player.quiz.xp_earned')}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: theme.typography.fontWeight.bold,
                color: quizTokens.xpText,
              }}
            >
              {t('student_course_player.quiz.xp_earned_value', {
                xp: result.xpEarned,
              })}
            </div>
          </div>
        </div>
        <div
          style={{
            borderTop: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
            paddingTop: theme.spacing.md,
            textAlign: 'center',
            fontSize: theme.typography.fontSize.sm,
            color: quizTokens.mutedText,
          }}
        >
          {t('student_course_player.quiz.minimum_required')}{' '}
          {formatScorePercent(result.minimumRequiredScore)}
        </div>
      </div>
      <button
        type="button"
        onClick={onPrimary}
        style={{
          width: '100%',
          border: 'none',
          borderRadius: theme.shape.borderRadius.sm,
          backgroundColor: primary,
          color: contrast,
          fontSize: theme.typography.fontSize.base,
          fontWeight: theme.typography.fontWeight.semibold,
          padding: `${theme.spacing.md} ${theme.spacing.lg}`,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {primaryLabel}
      </button>
    </div>
  );
}

function QuizResultFailureView({
  result,
  theme,
  primary,
  contrast,
  onTryAgain,
}: {
  result: QuizSubmitResultPayload;
  theme: ReturnType<typeof useTheme>;
  primary: string;
  contrast: string;
  onTryAgain: () => void;
}) {
  const { t } = useTranslation();
  const quizTokens = theme.palette.studentCoursePlayer.quiz;
  return (
    <div
      style={{
        maxWidth: 520,
        margin: '0 auto',
        padding: theme.spacing.lg,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 96,
          height: 96,
          margin: `0 auto ${theme.spacing.lg}`,
          borderRadius: '50%',
          backgroundColor: quizTokens.failBackground,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={iconQuizErrorStudent}
          alt=""
          width={56}
          height={56}
          style={{ display: 'block', objectFit: 'contain' }}
        />
      </div>
      <h2
        style={{
          margin: 0,
          fontSize: theme.typography.fontSize['3xl'],
          fontWeight: theme.typography.fontWeight.bold,
          color: quizTokens.failText,
        }}
      >
        {t('student_course_player.quiz.result_failure_title')}
      </h2>
      <p
        style={{
          marginTop: theme.spacing.sm,
          marginBottom: theme.spacing.lg,
          color: quizTokens.mutedText,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {t('student_course_player.quiz.result_failure_description')}
      </p>
      <div
        style={{
          borderRadius: theme.shape.borderRadius.md,
          border: `1px solid ${quizTokens.failText}`,
          backgroundColor: quizTokens.failBackground,
          padding: theme.spacing.lg,
          marginBottom: theme.spacing.lg,
          textAlign: 'left',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: theme.spacing.lg,
            marginBottom: theme.spacing.md,
          }}
        >
          <div>
            <div
              style={{
                fontSize: theme.typography.fontSize.sm,
                color: quizTokens.mutedText,
                marginBottom: theme.spacing.xs,
              }}
            >
              {t('student_course_player.quiz.your_score')}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: theme.typography.fontWeight.bold,
                color: quizTokens.failText,
              }}
            >
              {formatScorePercent(result.yourScore)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div
              style={{
                fontSize: theme.typography.fontSize.sm,
                color: quizTokens.mutedText,
                marginBottom: theme.spacing.xs,
              }}
            >
              {t('student_course_player.quiz.minimum_required')}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.palette.text.primary,
              }}
            >
              {formatScorePercent(result.minimumRequiredScore)}
            </div>
          </div>
        </div>
        <div
          style={{
            borderTop: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
            paddingTop: theme.spacing.md,
            textAlign: 'center',
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.palette.text.primary,
          }}
        >
          {t('student_course_player.quiz.attempts_remaining', {
            remaining: result.remainingAttempts,
            total: result.totalAttempts,
          })}
        </div>
      </div>
      <button
        type="button"
        onClick={onTryAgain}
        style={{
          width: '100%',
          border: 'none',
          borderRadius: theme.shape.borderRadius.sm,
          backgroundColor: primary,
          color: contrast,
          fontSize: theme.typography.fontSize.base,
          fontWeight: theme.typography.fontWeight.semibold,
          padding: `${theme.spacing.md} ${theme.spacing.lg}`,
          cursor: 'pointer',
          fontFamily: 'inherit',
          marginBottom: theme.spacing.md,
        }}
      >
        {t('student_course_player.quiz.try_again')}
      </button>
      <p
        style={{
          margin: 0,
          fontSize: theme.typography.fontSize.sm,
          color: quizTokens.mutedText,
          lineHeight: 1.5,
        }}
      >
        {t('student_course_player.quiz.retry_hint')}
      </p>
    </div>
  );
}

function StudentCoursePlayerQuizPanel({
  lessonTitle,
  xpLabel,
  durationLabel,
  quizPassingScorePercent,
  quizMaxAttempts,
  quizIntroHeadline,
  quizIntroDescription,
  quizQuestions,
  quizFetchQuestions,
  startAssessmentLabel,
  nextQuestionLabel,
  submitQuizLabel,
  previousLessonLabel,
  actionLabel,
  onPreviousLesson,
  onMarkComplete,
  quizOnSubmit,
  isLastLessonInCourse,
  quizOnNextLesson,
  quizOnFinishCourse,
  quizFetchPreAttempt,
}: {
  lessonTitle: string;
  xpLabel: string;
  durationLabel: string;
  quizPassingScorePercent: number;
  quizMaxAttempts: number;
  quizIntroHeadline: string;
  quizIntroDescription: string;
  quizQuestions: QuizQuestionItem[];
  quizFetchQuestions?: () => Promise<QuizQuestionItem[]>;
  quizFetchPreAttempt?: () => Promise<QuizPreAttemptPayload>;
  startAssessmentLabel: string;
  nextQuestionLabel: string;
  submitQuizLabel: string;
  previousLessonLabel: string;
  actionLabel: string;
  onPreviousLesson?: () => void;
  onMarkComplete?: () => void;
  quizOnSubmit?: (
    answers: QuizSubmitAnswerPayload[]
  ) => Promise<QuizSubmitResultPayload>;
  isLastLessonInCourse?: boolean;
  quizOnNextLesson?: () => void;
  quizOnFinishCourse?: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const quizTokens = theme.palette.studentCoursePlayer.quiz;
  const primary = theme.palette.primary.main;
  const contrast = theme.palette.primary.contrastText;
  const usesRemoteQuiz = quizFetchQuestions != null;
  const usesPreAttempt = quizFetchPreAttempt != null;

  const [phase, setPhase] = useState<QuizPhase>('intro');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  /** Respostas das questões já confirmadas com "Próxima" (índice da opção). */
  const [answersByIndex, setAnswersByIndex] = useState<Record<number, number>>(
    {}
  );
  const [remoteQuestions, setRemoteQuestions] = useState<QuizQuestionItem[]>([]);
  const [startLoading, setStartLoading] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<QuizSubmitResultPayload | null>(
    null
  );
  const [preAttempt, setPreAttempt] = useState<QuizPreAttemptPayload | null>(
    null
  );
  const [preAttemptLoading, setPreAttemptLoading] = useState(false);
  const [preAttemptError, setPreAttemptError] = useState<string | null>(null);

  const questionsForQuiz = usesRemoteQuiz ? remoteQuestions : quizQuestions;
  const totalQuestions = questionsForQuiz.length;

  useEffect(() => {
    setPhase('intro');
    setQuestionIndex(0);
    setSelectedOption(null);
    setAnswersByIndex({});
    setRemoteQuestions([]);
    setStartError(null);
    setStartLoading(false);
    setSubmitError(null);
    setSubmitLoading(false);
    setSubmitResult(null);
    setPreAttempt(null);
    setPreAttemptError(null);
    setPreAttemptLoading(false);
  }, [lessonTitle]);

  const reloadPreAttempt = useCallback(async () => {
    if (!quizFetchPreAttempt) return;
    setPreAttemptLoading(true);
    setPreAttemptError(null);
    try {
      const data = await quizFetchPreAttempt();
      setPreAttempt(data);
    } catch {
      setPreAttemptError(
        t('student_course_player.quiz.error_load_pre_attempt')
      );
      setPreAttempt(null);
    } finally {
      setPreAttemptLoading(false);
    }
  }, [quizFetchPreAttempt]);

  useEffect(() => {
    if (!quizFetchPreAttempt) return;
    void reloadPreAttempt();
  }, [lessonTitle, quizFetchPreAttempt, reloadPreAttempt]);

  const currentQuestion = questionsForQuiz[questionIndex];
  const isLastQuestion = questionIndex >= totalQuestions - 1;
  const canAdvance = selectedOption !== null && currentQuestion !== undefined;

  const handleStart = async () => {
    if (usesPreAttempt && preAttempt && preAttempt.remainingAttempts <= 0) {
      return;
    }
    if (quizFetchQuestions) {
      setStartLoading(true);
      setStartError(null);
      try {
        const loaded = await quizFetchQuestions();
        setRemoteQuestions(loaded);
        setPhase('active');
        setQuestionIndex(0);
        setSelectedOption(null);
        setAnswersByIndex({});
      } catch {
        setStartError(t('student_course_player.quiz.error_start'));
      } finally {
        setStartLoading(false);
      }
      return;
    }
    setPhase('active');
    setQuestionIndex(0);
    setSelectedOption(null);
    setAnswersByIndex({});
  };

  function buildSubmitAnswersPayload(
    questions: QuizQuestionItem[],
    mergedByIndex: Record<number, number>
  ): QuizSubmitAnswerPayload[] {
    return questions.map((q, i) => {
      const optIdx = mergedByIndex[i];
      const optionId = q.optionIds?.[optIdx];
      if (
        optIdx === undefined ||
        !q.questionId ||
        optionId === undefined
      ) {
        throw new Error('Quiz answer missing ids');
      }
      return {
        questionId: q.questionId,
        selectedOptionId: optionId,
      };
    });
  }

  const handleNext = async () => {
    if (!canAdvance || submitLoading) return;
    if (isLastQuestion) {
      if (quizOnSubmit) {
        setSubmitLoading(true);
        setSubmitError(null);
        try {
          const merged = { ...answersByIndex, [questionIndex]: selectedOption! };
          const payload = buildSubmitAnswersPayload(
            questionsForQuiz,
            merged
          );
          const result = await quizOnSubmit(payload);
          setSubmitResult(result);
          setPhase('result');
        } catch {
          setSubmitError(t('student_course_player.quiz.error_submit'));
        } finally {
          setSubmitLoading(false);
        }
        return;
      }
      setPhase('done');
      return;
    }
    setAnswersByIndex(prev => ({
      ...prev,
      [questionIndex]: selectedOption!,
    }));
    setQuestionIndex(i => i + 1);
    setSelectedOption(null);
  };

  const quizCardBorder = `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`;

  const introQuestionsLabel =
    usesPreAttempt && preAttemptLoading
      ? '—'
      : usesPreAttempt && preAttempt
        ? String(preAttempt.totalQuestions)
        : usesRemoteQuiz && phase === 'intro'
          ? '—'
          : String(totalQuestions);

  const effectivePassingPercent =
    usesPreAttempt && preAttempt
      ? preAttempt.minScore
      : quizPassingScorePercent;

  const effectiveMaxAttemptsDisplay =
    usesPreAttempt && preAttempt
      ? preAttempt.maxAttempts
      : quizMaxAttempts;

  const noAttemptsLeft =
    usesPreAttempt &&
    preAttempt !== null &&
    preAttempt.remainingAttempts <= 0;

  const startBlocked =
    preAttemptLoading ||
    (usesPreAttempt && preAttemptError != null) ||
    noAttemptsLeft;

  const handleQuizTryAgain = () => {
    setSubmitResult(null);
    setPhase('intro');
    setQuestionIndex(0);
    setSelectedOption(null);
    setAnswersByIndex({});
    setRemoteQuestions([]);
    setSubmitError(null);
    void reloadPreAttempt();
  };

  const handleSuccessPrimary = () => {
    if (isLastLessonInCourse) {
      quizOnFinishCourse?.();
    } else {
      quizOnNextLesson?.();
    }
  };

  if (phase === 'result' && submitResult) {
    return (
      <section
        style={{
          backgroundColor: theme.palette.background.paper,
          border: 'none',
          borderRight: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
          borderRadius: 0,
          overflow: 'auto',
          height: '100%',
          minHeight: 0,
        }}
      >
        <div style={{ padding: theme.spacing.lg }}>
          {submitResult.approved ? (
            <QuizResultSuccessView
              result={submitResult}
              isLastLessonInCourse={Boolean(isLastLessonInCourse)}
              primary={primary}
              contrast={contrast}
              theme={theme}
              onPrimary={handleSuccessPrimary}
            />
          ) : (
            <QuizResultFailureView
              result={submitResult}
              theme={theme}
              primary={primary}
              contrast={contrast}
              onTryAgain={handleQuizTryAgain}
            />
          )}
        </div>
      </section>
    );
  }

  if (phase === 'done') {
    return (
      <section
        style={{
          backgroundColor: theme.palette.background.paper,
          border: 'none',
          borderRight: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
          borderRadius: 0,
          overflow: 'auto',
          height: '100%',
          minHeight: 0,
        }}
      >
        <div style={{ padding: theme.spacing.lg }}>
          <h2
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize['3xl'],
              color: theme.palette.text.primary,
            }}
          >
            {lessonTitle}
          </h2>
          <p
            style={{
              marginTop: theme.spacing.md,
              color: theme.palette.text.secondary,
              fontSize: theme.typography.fontSize.sm,
            }}
          >
            {t('student_course_player.quiz.assessment_submitted')}
          </p>
          <ContentFooter
            previousLessonLabel={previousLessonLabel}
            actionLabel={actionLabel}
            onPreviousLesson={onPreviousLesson}
            onMarkComplete={onMarkComplete}
          />
        </div>
      </section>
    );
  }

  if (phase === 'intro') {
    return (
      <section
        style={{
          backgroundColor: theme.palette.background.paper,
          border: 'none',
          borderRight: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
          borderRadius: 0,
          overflow: 'auto',
          height: '100%',
          minHeight: 0,
        }}
      >
        <div style={{ padding: theme.spacing.lg }}>
          <h2
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize['3xl'],
              lineHeight: theme.typography.lineHeight.tight,
              color: theme.palette.text.primary,
            }}
          >
            {lessonTitle}
          </h2>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.sm,
              marginTop: theme.spacing.sm,
              marginBottom: theme.spacing.lg,
            }}
          >
            <span
              style={{
                fontSize: theme.typography.fontSize.xs,
                fontWeight: theme.typography.fontWeight.normal,
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.primary.main,
                borderRadius: theme.shape.borderRadius.full,
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
              }}
            >
              {xpLabel}
            </span>
            <span
              style={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              {durationLabel}
            </span>
          </div>

          <div
            style={{
              border: quizCardBorder,
              borderRadius: theme.shape.borderRadius.md,
              padding: theme.spacing.xl,
              backgroundColor: theme.palette.background.paper,
              maxWidth: 560,
              margin: '0 auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <img
                src={iconQuizSmallModalCourses}
                alt=""
                width={56}
                height={56}
                style={{
                  display: 'block',
                  objectFit: 'contain',
                  marginBottom: theme.spacing.md,
                }}
              />
              <h3
                style={{
                  margin: 0,
                  fontSize: theme.typography.fontSize.xl,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.palette.text.primary,
                }}
              >
                {quizIntroHeadline}
              </h3>
              <p
                style={{
                  margin: `${theme.spacing.sm} 0 ${theme.spacing.lg} 0`,
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.fontSize.sm,
                  lineHeight: 1.5,
                  maxWidth: 420,
                }}
              >
                {quizIntroDescription}
              </p>
            </div>

            <div
              style={{
                borderTop: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
                borderBottom: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
                padding: `${theme.spacing.md} 0`,
                marginBottom: theme.spacing.md,
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: theme.spacing.sm,
                  textAlign: 'center',
                }}
              >
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {t('student_course_player.quiz.questions')}
                  </p>
                  <p
                    style={{
                      margin: `${theme.spacing.xs2} 0 0 0`,
                      fontSize: theme.typography.fontSize['2xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {introQuestionsLabel}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {t('student_course_player.quiz.passing_score')}
                  </p>
                  <p
                    style={{
                      margin: `${theme.spacing.xs2} 0 0 0`,
                      fontSize: theme.typography.fontSize['2xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {effectivePassingPercent}%
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {t('student_course_player.quiz.max_attempts')}
                  </p>
                  <p
                    style={{
                      margin: `${theme.spacing.xs2} 0 0 0`,
                      fontSize: theme.typography.fontSize['2xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {effectiveMaxAttemptsDisplay}
                  </p>
                </div>
              </div>
            </div>

            {usesPreAttempt &&
            preAttempt &&
            !preAttemptLoading &&
            !noAttemptsLeft ? (
              <div
                style={{
                  backgroundColor: quizTokens.failBackground,
                  borderRadius: theme.shape.borderRadius.sm,
                  padding: theme.spacing.md,
                  marginBottom: theme.spacing.md,
                  textAlign: 'center',
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: quizTokens.failText,
                    lineHeight: 1.5,
                  }}
                >
                  {t('student_course_player.quiz.attempts_remaining', {
                    remaining: preAttempt.remainingAttempts,
                    total: preAttempt.maxAttempts,
                  })}
                </p>
              </div>
            ) : null}

            {noAttemptsLeft ? (
              <div
                role="alert"
                style={{
                  backgroundColor: quizTokens.failBackground,
                  border: `${theme.shape.borderWidth.thin} solid ${quizTokens.failText}`,
                  borderRadius: theme.shape.borderRadius.sm,
                  padding: theme.spacing.md,
                  marginBottom: theme.spacing.md,
                  textAlign: 'center',
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: quizTokens.failText,
                    lineHeight: 1.5,
                  }}
                >
                  {t('student_course_player.quiz.no_attempts_remaining')}
                </p>
              </div>
            ) : null}

            {preAttemptError ? (
              <p
                style={{
                  margin: `0 0 ${theme.spacing.md} 0`,
                  fontSize: theme.typography.fontSize.sm,
                  color: quizTokens.failText,
                  textAlign: 'center',
                }}
              >
                {preAttemptError}
              </p>
            ) : null}

            {startError ? (
              <p
                style={{
                  margin: `0 0 ${theme.spacing.md} 0`,
                  fontSize: theme.typography.fontSize.sm,
                  color: quizTokens.errorText,
                  textAlign: 'center',
                }}
              >
                {startError}
              </p>
            ) : null}

            <div
              style={{
                backgroundColor: quizTokens.noteBackground,
                borderRadius: theme.shape.borderRadius.sm,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                textAlign: 'left',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.palette.text.secondary,
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ color: theme.palette.text.primary }}>
                  {t('student_course_player.quiz.note_label')}
                </strong>{' '}
                {t('student_course_player.quiz.note_intro', {
                  passingPercent: effectivePassingPercent,
                })}
                {usesPreAttempt && preAttempt
                  ? ` ${t('student_course_player.quiz.note_attempts_remaining', {
                      remaining: preAttempt.remainingAttempts,
                      total: preAttempt.maxAttempts,
                    })}`
                  : ` ${t('student_course_player.quiz.note_attempts_available', {
                      total: quizMaxAttempts,
                    })}`}
              </p>
            </div>

            {!noAttemptsLeft ? (
              <button
                type="button"
                disabled={startLoading || startBlocked}
                onClick={() => {
                  void handleStart();
                }}
                style={{
                  width: '100%',
                  border: 'none',
                  borderRadius: theme.shape.borderRadius.sm,
                  backgroundColor: primary,
                  color: contrast,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.bold,
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  cursor:
                    startLoading || startBlocked ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  opacity: startLoading || startBlocked ? 0.65 : 1,
                }}
              >
                {startLoading || preAttemptLoading
                  ? t('student_course_player.common.loading')
                  : startAssessmentLabel}
              </button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      style={{
        backgroundColor: theme.palette.background.paper,
        border: 'none',
        borderRight: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
        borderRadius: 0,
        overflow: 'auto',
        height: '100%',
        minHeight: 0,
      }}
    >
      <div style={{ padding: theme.spacing.lg }}>
        <h2
          style={{
            margin: 0,
            fontSize: theme.typography.fontSize['3xl'],
            lineHeight: theme.typography.lineHeight.tight,
            color: theme.palette.text.primary,
          }}
        >
          {lessonTitle}
        </h2>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: theme.spacing.sm,
            marginTop: theme.spacing.sm,
            marginBottom: theme.spacing.lg,
          }}
        >
          <span
            style={{
              fontSize: theme.typography.fontSize.xs,
              fontWeight: theme.typography.fontWeight.normal,
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
              borderRadius: theme.shape.borderRadius.full,
              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            }}
          >
            {xpLabel}
          </span>
          <span
            style={{
              fontSize: theme.typography.fontSize.xs,
              fontWeight: theme.typography.fontWeight.normal,
              color: quizTokens.questionCounterText,
              backgroundColor: quizTokens.questionCounterBackground,
              border: `${theme.shape.borderWidth.thin} solid ${quizTokens.questionCounterText}`,
              borderRadius: theme.shape.borderRadius.full,
              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            }}
          >
            {t('student_course_player.quiz.question_of', {
              current: questionIndex + 1,
              total: totalQuestions,
            })}
          </span>
        </div>

        <div
          style={{
            border: quizCardBorder,
            borderRadius: theme.shape.borderRadius.md,
            padding: theme.spacing.lg,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {currentQuestion && (
            <>
              <h3
                style={{
                  margin: `0 0 ${theme.spacing.lg} 0`,
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.palette.text.primary,
                }}
              >
                {currentQuestion.prompt}
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing.sm,
                }}
              >
                {currentQuestion.options.map((opt, optIndex) => {
                  const id = `quiz-q${questionIndex}-opt${optIndex}`;
                  const checked = selectedOption === optIndex;
                  const rowBorder = checked
                    ? `${theme.shape.borderWidth.medium} solid ${theme.palette.primary.main}`
                    : `${theme.shape.borderWidth.medium} solid ${theme.palette.border.main}`;
                  return (
                    <label
                      key={id}
                      htmlFor={id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing.md,
                        padding: theme.spacing.sm2,
                        boxSizing: 'border-box',
                        border: rowBorder,
                        borderRadius: theme.shape.borderRadius.sm,
                        backgroundColor: theme.palette.background.paper,
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          position: 'relative',
                          width: 18,
                          height: 18,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <input
                          id={id}
                          type="radio"
                          name={`quiz-question-${questionIndex}`}
                          checked={checked}
                          onChange={() => setSelectedOption(optIndex)}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: 18,
                            height: 18,
                            margin: 0,
                            cursor: 'pointer',
                            zIndex: 1,
                          }}
                        />
                        <span
                          aria-hidden
                          style={{
                            width: 18,
                            height: 18,
                            minWidth: 18,
                            borderRadius: '50%',
                            boxSizing: 'border-box',
                            border: checked
                              ? `${theme.shape.borderWidth.medium} solid ${theme.palette.primary.main}`
                              : `${theme.shape.borderWidth.medium} solid ${theme.palette.border.main}`,
                            backgroundColor: checked
                              ? theme.palette.primary.main
                              : theme.palette.background.paper,
                            pointerEvents: 'none',
                          }}
                        />
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: theme.typography.fontWeight.normal,
                          color: theme.palette.text.primary,
                          lineHeight: 1.4,
                        }}
                      >
                        {opt}
                      </span>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {submitError && (
            <p
              role="alert"
              style={{
                marginTop: theme.spacing.md,
                marginBottom: 0,
                color: quizTokens.errorText,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              {submitError}
            </p>
          )}
          <div
            style={{
              marginTop: theme.spacing.lg,
              paddingTop: theme.spacing.md,
              borderTop: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              disabled={!canAdvance || submitLoading}
              onClick={() => void handleNext()}
              style={{
                border: 'none',
                borderRadius: theme.shape.borderRadius.sm,
                backgroundColor:
                  canAdvance && !submitLoading
                    ? primary
                    : `${theme.palette.primary.main}73`,
                color: contrast,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.semibold,
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                cursor:
                  canAdvance && !submitLoading ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                opacity: canAdvance && !submitLoading ? 1 : 0.85,
              }}
            >
              {submitLoading
                ? t('student_course_player.common.loading_short')
                : isLastQuestion
                  ? submitQuizLabel
                  : nextQuestionLabel}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContentFooter({
  previousLessonLabel,
  actionLabel,
  onPreviousLesson,
  onMarkComplete,
}: {
  previousLessonLabel: string;
  actionLabel: string;
  onPreviousLesson?: () => void;
  onMarkComplete?: () => void;
}) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const contrast = theme.palette.primary.contrastText;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing.md,
        marginTop: theme.spacing.lg,
        paddingTop: theme.spacing.lg,
        borderTop: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
      }}
    >
      <button
        type="button"
        onClick={onPreviousLesson}
        style={{
          border: `${theme.shape.borderWidth.thin} solid ${theme.palette.text.primary}`,
          borderRadius: theme.shape.borderRadius.sm,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          fontSize: theme.typography.fontSize.sm,
          fontWeight: theme.typography.fontWeight.semibold,
          padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {previousLessonLabel}
      </button>
      <button
        type="button"
        onClick={onMarkComplete}
        style={{
          border: 'none',
          borderRadius: theme.shape.borderRadius.sm,
          backgroundColor: primary,
          color: contrast,
          fontSize: theme.typography.fontSize.sm,
          fontWeight: theme.typography.fontWeight.semibold,
          padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {actionLabel}
      </button>
    </div>
  );
}

export function StudentCoursePlayerContentMock({
  activityType,
  lessonTitle,
  durationLabel,
  xpLabel,
  actionLabel,
  resourceDescription = DEFAULT_RESOURCE_DESCRIPTION,
  downloadableResources = [],
  quizPassingScorePercent = 70,
  quizMaxAttempts = 3,
  quizIntroHeadline,
  quizIntroDescription,
  quizQuestions = DEFAULT_QUIZ_QUESTIONS,
  quizFetchQuestions,
  quizFetchPreAttempt,
  startAssessmentLabel,
  nextQuestionLabel,
  submitQuizLabel,
  previousLessonLabel,
  vimeoUrl,
  initialVideoTimeSeconds,
  initialVideoProgressPercentage,
  onVideoProgress,
  onPreviousLesson,
  onMarkComplete,
  quizOnSubmit,
  isLastLessonInCourse,
  quizOnNextLesson,
  quizOnFinishCourse,
}: StudentCoursePlayerContentProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const contrast = theme.palette.primary.contrastText;
  const playerContent = theme.palette.studentCoursePlayer.content;
  const effectiveQuizIntroHeadline =
    quizIntroHeadline ?? t('student_course_player.quiz.intro_headline');
  const effectiveQuizIntroDescription =
    quizIntroDescription ?? t('student_course_player.quiz.intro_description');
  const effectiveStartAssessmentLabel =
    startAssessmentLabel ?? t('student_course_player.quiz.start_assessment');
  const effectiveNextQuestionLabel =
    nextQuestionLabel ?? t('student_course_player.quiz.next_question');
  const effectiveSubmitQuizLabel =
    submitQuizLabel ?? t('student_course_player.quiz.submit');
  const effectivePreviousLessonLabel =
    previousLessonLabel ?? t('student_course_player.common.previous_lesson');

  if (activityType === 'resource') {
    return (
      <section
        style={{
          backgroundColor: theme.palette.background.paper,
          border: 'none',
          borderRight: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
          borderRadius: 0,
          overflow: 'auto',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: theme.spacing.lg,
            flex: 1,
            minHeight: 0,
            boxSizing: 'border-box',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize['3xl'],
              lineHeight: theme.typography.lineHeight.tight,
              color: theme.palette.text.primary,
            }}
          >
            {lessonTitle}
          </h2>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.sm,
              marginTop: theme.spacing.sm,
              marginBottom: theme.spacing.md,
            }}
          >
            <span
              style={{
                fontSize: theme.typography.fontSize.xs,
                fontWeight: theme.typography.fontWeight.bold,
                color: primary,
                backgroundColor: `${theme.palette.primary.main}26`,
                borderRadius: theme.shape.borderRadius.full,
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
              }}
            >
              {xpLabel}
            </span>
            <span
              style={{
                color: theme.palette.text.secondary,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              {durationLabel}
            </span>
          </div>
          <p
            style={{
              margin: `0 0 ${theme.spacing.lg} 0`,
              color: theme.palette.text.secondary,
              fontSize: theme.typography.fontSize.sm,
              lineHeight: 1.5,
              maxWidth: 720,
            }}
          >
            {resourceDescription}
          </p>

          <div
            style={{
              border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
              borderRadius: theme.shape.borderRadius.md,
              padding: theme.spacing.lg,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: theme.spacing.md,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.palette.text.primary,
                }}
              >
                {t('student_course_player.resource.downloadable_resources')}
              </h3>
              <img
                src={iconFileModalCourses}
                alt=""
                width={24}
                height={24}
                style={{
                  display: 'block',
                  objectFit: 'contain',
                  opacity: 0.65,
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.sm,
              }}
            >
              {downloadableResources.map(file => (
                <div
                  key={`${file.fileName}-${file.fileUrl ?? 'local'}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.md,
                    padding: theme.spacing.sm2,
                    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
                    borderRadius: theme.shape.borderRadius.sm,
                    backgroundColor: theme.palette.background.default,
                  }}
                >
                  <FileTypeBadge kind={file.kind} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: theme.typography.fontSize.sm,
                        fontWeight: theme.typography.fontWeight.semibold,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {file.fileName}
                    </p>
                    <p
                      style={{
                        margin: `${theme.spacing.xs2} 0 0 0`,
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {file.sizeLabel}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!file.fileUrl) return;
                      window.open(
                        file.fileUrl,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                    disabled={!file.fileUrl}
                    style={{
                      border: 'none',
                      borderRadius: theme.shape.borderRadius.sm,
                      backgroundColor: primary,
                      color: contrast,
                      fontSize: theme.typography.fontSize.xs,
                      fontWeight: theme.typography.fontWeight.semibold,
                      padding: `${theme.spacing.xs2} ${theme.spacing.md}`,
                      cursor: file.fileUrl ? 'pointer' : 'not-allowed',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: theme.spacing.xs,
                      flexShrink: 0,
                      fontFamily: 'inherit',
                      whiteSpace: 'nowrap',
                      opacity: file.fileUrl ? 1 : 0.6,
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 14 }}>
                      ↓
                    </span>
                    {t('student_course_player.resource.download')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <ContentFooter
            previousLessonLabel={effectivePreviousLessonLabel}
            actionLabel={actionLabel}
            onPreviousLesson={onPreviousLesson}
            onMarkComplete={onMarkComplete}
          />
        </div>
      </section>
    );
  }

  if (activityType === 'quiz') {
    return (
      <StudentCoursePlayerQuizPanel
        lessonTitle={lessonTitle}
        xpLabel={xpLabel}
        durationLabel={durationLabel}
        quizPassingScorePercent={quizPassingScorePercent}
        quizMaxAttempts={quizMaxAttempts}
        quizIntroHeadline={effectiveQuizIntroHeadline}
        quizIntroDescription={effectiveQuizIntroDescription}
        quizQuestions={quizQuestions}
        quizFetchQuestions={quizFetchQuestions}
        quizFetchPreAttempt={quizFetchPreAttempt}
        startAssessmentLabel={effectiveStartAssessmentLabel}
        nextQuestionLabel={effectiveNextQuestionLabel}
        submitQuizLabel={effectiveSubmitQuizLabel}
        previousLessonLabel={effectivePreviousLessonLabel}
        actionLabel={actionLabel}
        onPreviousLesson={onPreviousLesson}
        onMarkComplete={onMarkComplete}
        quizOnSubmit={quizOnSubmit}
        isLastLessonInCourse={isLastLessonInCourse}
        quizOnNextLesson={quizOnNextLesson}
        quizOnFinishCourse={quizOnFinishCourse}
      />
    );
  }

  return (
    <section
      style={{
        backgroundColor: theme.palette.background.paper,
        border: 'none',
        borderRight: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
        borderRadius: 0,
        overflow: 'hidden',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          width: '100%',
          aspectRatio: '16 / 9',
          minHeight: 320,
          maxHeight: 'min(70vh, 720px)',
          backgroundColor: playerContent.videoUnavailableBackground,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {vimeoUrl ? (
          <VimeoPlayer
            onProgress={onVideoProgress}
            vimeoUrl={vimeoUrl}
            initialTime={initialVideoTimeSeconds}
            initialProgressPercentage={initialVideoProgressPercentage}
            style={{ height: '100%' }}
          />
        ) : (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.background.paper,
              fontSize: theme.typography.fontSize.sm,
            }}
          >
            {t('student_course_player.video.unavailable')}
          </div>
        )}
      </div>

      <div
        style={{
          padding: theme.spacing.lg,
          flex: 1,
          minHeight: 0,
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: theme.typography.fontSize['3xl'],
            lineHeight: theme.typography.lineHeight.tight,
            color: theme.palette.text.primary,
          }}
        >
          {lessonTitle}
        </h2>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm,
            marginTop: theme.spacing.sm,
            marginBottom: theme.spacing.lg,
          }}
        >
          <span
            style={{
              fontSize: theme.typography.fontSize.xs,
              fontWeight: theme.typography.fontWeight.bold,
              color: primary,
              backgroundColor: `${theme.palette.primary.main}26`,
              borderRadius: theme.shape.borderRadius.full,
              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            }}
          >
            {xpLabel}
          </span>
          <span
            style={{
              color: theme.palette.text.secondary,
              fontSize: theme.typography.fontSize.sm,
            }}
          >
            {durationLabel}
          </span>
        </div>
        <ContentFooter
          previousLessonLabel={effectivePreviousLessonLabel}
          actionLabel={actionLabel}
          onPreviousLesson={onPreviousLesson}
          onMarkComplete={onMarkComplete}
        />
      </div>
    </section>
  );
}
